import { FormItem, FormLabel, FormMessage } from "@workspace/web-ui/components/form";
import { FormControl } from "@workspace/web-ui/components/form";
import { Input } from "@workspace/web-ui/components/input";
import { AlertCircle, KeyRound, KeySquare } from "lucide-react";
import { Control, Controller, ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { FormFieldType } from "../../types"
import { PasswordInput } from "@workspace/web-ui/components/password";
import { Textarea } from "@workspace/web-ui/components/textarea";
import { JSX, ReactNode } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select";

/**
 * Base properties for form field components
 */
interface BaseFieldProps {
    label?: string;
    placeholder?: string;
    type?: string;
    defaultValue?: unknown;
    error?: string;
    className?: string;
    autoComplete?: string;
    maxLength?: number;
    options?: {
        label: string;
        value: string;
    }[];
}

/**
 * Props for the FormField component
 */
interface FormFieldProps<T extends FieldValues> extends BaseFieldProps {
    control: Control<T>;
    name: Path<T>;
    fieldType: FormFieldType;
    customRender?: (args: { field: ControllerRenderProps<T, Path<T>>; error?: string; props: FormFieldProps<T> }) => JSX.Element;
}

/**
 * Props for the field renderer
 */
interface RenderFieldProps<T extends FieldValues> extends FormFieldProps<T> {
    field: ControllerRenderProps<T, Path<T>>;
}

/**
 * Renders a textarea field with optional character count
 */
function renderTextArea<T extends FieldValues>(props: RenderFieldProps<T>): ReactNode {
    const value = props.field.value || "";
    const characterCount = value.length;
    const maxLength = props.maxLength;
    const hasMaxLength = maxLength !== undefined;
    const isLimitReached = hasMaxLength && characterCount >= maxLength;
    const isNearLimit = hasMaxLength && characterCount > (maxLength * 0.75);

    const getCounterColor = (): string => {
        if (isLimitReached) return "text-red-500";
        if (isNearLimit) return "text-yellow-600";
        return "text-gray-500";
    };

    return (
        <div>
            <div className="relative">
                <Textarea
                    placeholder={props.placeholder || "Type your message here..."}
                    className={`resize-none min-h-[120px] ${
                        hasMaxLength ? "pr-16" : ""
                    } ${
                        isLimitReached ? "border-red-500 focus-visible:ring-red-500" : ""
                    } ${props.className || ""}`}
                    aria-label={props.label}
                    aria-invalid={!!props.error || isLimitReached}
                    {...(hasMaxLength && { maxLength })}
                    {...props.field}
                />
                {hasMaxLength && (
                    <div className={`absolute bottom-3 right-3 text-xs font-medium ${getCounterColor()}`}>
                        {characterCount}/{maxLength}
                    </div>
                )}
            </div>

            {isLimitReached && (
                <div className="text-red-500 text-sm flex gap-2 items-center mt-2">
                    <AlertCircle className="w-4 h-4" />
                    Message cannot exceed {maxLength} characters
                </div>
            )}
        </div>
    );
}

function renderSelect<T extends FieldValues>(props: RenderFieldProps<T>): ReactNode {
    return (
        <Select 
            onValueChange={props.field.onChange}
            value={props.field.value}
            defaultValue={props.field.value}
        >
            <SelectTrigger className={props.className}>
                <SelectValue placeholder={props.placeholder} />
            </SelectTrigger>
            <SelectContent>
                {props.options?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/**
 * Renders the appropriate form field based on the field type
 */
function renderField<T extends FieldValues>(props: RenderFieldProps<T>): ReactNode {
    switch (props.fieldType) {
        case FormFieldType.TEXT:
            return (
                <Input
                    type={props.type}
                    autoComplete={props.autoComplete}
                    className={props.className}
                    placeholder={props.placeholder}
                    aria-label={props.label}
                    aria-invalid={!!props.error}
                    {...props.field}
                />
            );
        case FormFieldType.PASSWORD:
            return (
                <PasswordInput
                    placeholder="Enter password"
                    type="password"
                    autoComplete="current-password"
                    className={props.className}
                    aria-label="Password"
                    aria-invalid={!!props.error}
                    showIcon={<KeyRound size={16} />}
                    hideIcon={<KeySquare size={16} />}
                    {...props.field}
                />
            );
        case FormFieldType.TEXTAREA:
            return renderTextArea(props);
        case FormFieldType.SELECT:
            return renderSelect(props);
        default:
            return (
                <Input
                    placeholder={props.placeholder}
                    type={props.type}
                    autoComplete={props.autoComplete}
                    className={props.className}
                    aria-label={props.label}
                    aria-invalid={!!props.error}
                    {...props.field}
                />
            );
    }
}

/**
 * A generic form field component that supports different input types
 * and integrates with react-hook-form
 */
function FormField<T extends FieldValues>(props: FormFieldProps<T>): JSX.Element {
    return (
        <Controller
            control={props.control}
            name={props.name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel className="text-[#1A202C] font-medium text-base">
                        {props.label}
                    </FormLabel>
                    <FormControl>
                        {props.customRender
                            ? props.customRender({ field, error: props.error, props })
                            : renderField({ ...props, field })}
                    </FormControl>
                    {props.error && (
                        <FormMessage className="text-red-500 text-sm flex gap-2 items-center">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            {props.error}
                        </FormMessage>
                    )}
                </FormItem>
            )}
        />
    );
}

export default FormField;